<template>
  <v-container class="mt-3">
    <!-- Community Info -->
    <v-row v-if="community" class="mb-6">
      <v-col cols="12">
        <v-card>
          <v-card-text class="d-flex align-center">
            <v-icon
              icon="mdi-office-building"
              size="24"
              class="me-3"
              color="primary"
            ></v-icon>
            <div>
              <div class="text-h6">{{ community.title || community.name }}</div>
              <div class="d-flex align-center text-subtitle-2 text-medium-emphasis">
                <span>{{ community.address || $t('settings.notSpecified') }}</span>
                <v-divider
                  vertical
                  class="mx-2"
                  thickness="2"
                ></v-divider>
                <div class="d-flex align-center">
                  <v-icon
                    icon="mdi-account-group"
                    size="16"
                    class="me-1"
                  ></v-icon>
                  {{ usersCount }} {{ $t('dashboard.users') }}
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Community Selector for Admin -->
    <v-row>
      <v-col v-if="userStore.isAdmin" cols="12">
        <v-card>
          <v-card-text>
            <v-select
              v-model="selectedCommunityId"
              :items="communities"
              item-title="title"
              item-value="value"
              :label="$t('settings.selectCommunity')"
              variant="outlined"
              density="comfortable"
              hide-details
              class="community-select"
              @update:model-value="handleCommunityChange"
            ></v-select>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Statistics -->
    <v-row>
      <!-- Locked Packages -->
      <v-col cols="12" md="4">
        <v-card class="stat-card">
          <div class="stat-icon-wrapper blue-gradient">
            <v-icon
              icon="mdi-package-variant-closed"
              size="36"
              color="white"
            ></v-icon>
          </div>
          <v-card-text class="stat-content">
            <div class="stat-title">{{ $t('dashboard.lockedPackages') }}</div>
            <div class="stat-value">
              <div v-if="loading" class="d-flex align-center">
                <v-progress-circular
                  indeterminate
                  size="24"
                  width="2"
                  color="primary"
                ></v-progress-circular>
              </div>
              <div v-else class="d-flex align-center">
                <span class="me-2">{{ statistics.locked || 0 }}</span>
                <v-btn
                  icon
                  variant="text"
                  density="comfortable"
                  color="primary"
                  size="small"
                  @click="navigateToPackages('locked')"
                  class="stat-link-btn"
                >
                  <v-icon>mdi-arrow-right-circle</v-icon>
                  <v-tooltip
                    activator="parent"
                    location="top"
                  >
                    {{ $t('dashboard.viewAllActive') }}
                  </v-tooltip>
                </v-btn>
              </div>
            </div>
            <div class="stat-description">{{ $t('dashboard.currentlyStored') }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Expired Packages -->
      <v-col cols="12" md="4">
        <v-card class="stat-card">
          <div class="stat-icon-wrapper orange-gradient">
            <v-icon
              icon="mdi-clock-alert"
              size="36"
              color="white"
            ></v-icon>
          </div>
          <v-card-text class="stat-content">
            <div class="stat-title">{{ $t('dashboard.expiredPackages') }}</div>
            <div class="stat-value">
              <div v-if="loading" class="d-flex align-center">
                <v-progress-circular
                  indeterminate
                  size="24"
                  width="2"
                  color="warning"
                ></v-progress-circular>
              </div>
              <div v-else class="d-flex align-center">
                <span class="me-2">{{ statistics.expired || 0 }}</span>
                <v-btn
                  icon
                  variant="text"
                  density="comfortable"
                  color="warning"
                  size="small"
                  @click="navigateToPackages('expired')"
                  class="stat-link-btn"
                >
                  <v-icon>mdi-arrow-right-circle</v-icon>
                  <v-tooltip
                    activator="parent"
                    location="top"
                  >
                    {{ $t('dashboard.viewAllExpired') }}
                  </v-tooltip>
                </v-btn>
              </div>
            </div>
            <div class="stat-description">{{ $t('dashboard.storageExpired') }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Finished Packages -->
      <v-col cols="12" md="4">
        <v-card class="stat-card">
          <div class="stat-icon-wrapper green-gradient">
            <v-icon
              icon="mdi-package-variant-closed-check"
              size="36"
              color="white"
            ></v-icon>
          </div>
          <v-card-text class="stat-content">
            <div class="stat-title">{{ $t('dashboard.finishedPackages') }}</div>
            <div class="stat-value">
              <div v-if="loading" class="d-flex align-center">
                <v-progress-circular
                  indeterminate
                  size="24"
                  width="2"
                  color="success"
                ></v-progress-circular>
              </div>
              <div v-else class="d-flex align-center">
                <span class="me-2">{{ statistics.finished || 0 }}</span>
                <v-btn
                  icon
                  variant="text"
                  density="comfortable"
                  color="success"
                  size="small"
                  @click="navigateToPackages('finished')"
                  class="stat-link-btn"
                >
                  <v-icon>mdi-arrow-right-circle</v-icon>
                  <v-tooltip
                    activator="parent"
                    location="top"
                  >
                    {{ $t('dashboard.viewAllFinished') }}
                  </v-tooltip>
                </v-btn>
              </div>
            </div>
            <div class="stat-description">{{ $t('dashboard.successfullyDelivered') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Lockers & Cells Statistics -->
    <v-row class="mt-6">
      <!-- Lockers Status -->
      <v-col cols="12" md="6">
        <v-card>
          <v-card-text class="pa-6">
            <div class="d-flex align-center mb-4">
              <v-icon
                icon="mdi-server"
                size="24"
                color="primary"
                class="me-2"
              ></v-icon>
              <span class="text-h6 font-weight-medium">{{ $t('dashboard.lockersStatus') }}</span>
            </div>
            <div class="stats-grid">
              <div class="status-item total">
                <span class="status-value">{{ lockersStats.lockers || 0 }}</span>
                <span class="status-label">{{ $t('dashboard.totalLockers') }}</span>
              </div>
              <div class="status-item success">
                <v-icon
                  icon="mdi-check-circle"
                  size="20"
                  class="status-icon"
                ></v-icon>
                <span class="status-value">{{ lockersStats.online_lockers || 0 }}</span>
                <span class="status-label">{{ $t('dashboard.onlineLockers') }}</span>
              </div>
              <div class="status-item warning">
                <v-icon
                  icon="mdi-wifi-off"
                  size="20"
                  class="status-icon"
                ></v-icon>
                <span class="status-value">{{ lockersStats.offline_lockers || 0 }}</span>
                <span class="status-label">{{ $t('dashboard.offlineLockers') }}</span>
              </div>
              <div class="status-item info">
                <v-icon
                  icon="mdi-wrench"
                  size="20"
                  class="status-icon"
                ></v-icon>
                <span class="status-value">{{ lockersStats.maintenance_lockers || 0 }}</span>
                <span class="status-label">{{ $t('dashboard.maintenanceLockers') }}</span>
              </div>
              <div class="status-item error">
                <v-icon
                  icon="mdi-alert-circle"
                  size="20"
                  class="status-icon"
                ></v-icon>
                <span class="status-value">{{ lockersStats.error_lockers || 0 }}</span>
                <span class="status-label">{{ $t('dashboard.errorLockers') }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <!-- Cells Status -->
      <v-col cols="12" md="6">
        <v-card>
          <v-card-text class="pa-6">
            <div class="d-flex align-center mb-4">
              <v-icon
                icon="mdi-grid"
                size="24"
                color="primary"
                class="me-2"
              ></v-icon>
              <span class="text-h6 font-weight-medium">{{ $t('dashboard.cellsStatus') }}</span>
            </div>
            <div class="stats-grid">
              <div class="status-item total">
                <span class="status-value">{{ lockersStats.cells || 0 }}</span>
                <span class="status-label">{{ $t('dashboard.totalCells') }}</span>
              </div>
              <div class="status-item success">
                <v-icon
                  icon="mdi-check-circle"
                  size="20"
                  class="status-icon"
                ></v-icon>
                <span class="status-value">{{ lockersStats.active_cells || 0 }}</span>
                <span class="status-label">{{ $t('dashboard.activeCells') }}</span>
              </div>
              <div class="status-item error">
                <v-icon
                  icon="mdi-close-circle"
                  size="20"
                  class="status-icon"
                ></v-icon>
                <span class="status-value">{{ lockersStats.broken_cells || 0 }}</span>
                <span class="status-label">{{ $t('dashboard.brokenCells') }}</span>
              </div>
              <div class="status-item warning">
                <v-icon
                  icon="mdi-lock"
                  size="20"
                  class="status-icon"
                ></v-icon>
                <span class="status-value">{{ lockersStats.occupied_cells || 0 }}</span>
                <span class="status-label">{{ $t('dashboard.occupiedCells') }}</span>
              </div>
              <div class="status-item info">
                <span class="status-value">{{ lockersStats.rented_cells || 0 }}</span>
                <span class="status-label">{{ $t('dashboard.reservedCells') }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Occupancy Chart -->
    <v-row class="mt-6">
      <v-col cols="12">
        <v-card>
          <v-card-text class="pa-6">
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center">
                <v-icon
                  icon="mdi-chart-line"
                  size="24"
                  color="primary"
                  class="me-2"
                ></v-icon>
                <span class="text-h6 font-weight-medium">{{ $t('dashboard.occupancyChart') }}</span>
              </div>
              <v-btn-toggle
                v-model="selectedDays"
                mandatory
                density="compact"
                rounded="lg"
                color="primary"
                class="period-toggle"
                border
                @update:model-value="handlePeriodChange"
              >
                <v-btn
                  v-for="option in dayOptions"
                  :key="option.value"
                  :value="option.value"
                  variant="text"
                  class="period-btn"
                  density="compact"
                >
                  {{ $t(option.title) }}
                </v-btn>
              </v-btn-toggle>
            </div>
            <div class="chart-container">
              <Line
                v-if="chartReady"
                :data="chartData"
                :options="chartOptions"
              />
              <div v-else-if="chartLoading" class="d-flex justify-center align-center" style="height: 400px">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>
              <div v-else class="d-flex justify-center align-center" style="height: 400px">
                <span class="text-medium-emphasis">No data available</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <div class="mb-16"></div>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import api from '@/services/api'
import { useUserStore } from '@/stores/user'
import { usePackageStore } from '@/stores/package'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

const userStore = useUserStore()
const packageStore = usePackageStore()
const { t } = useI18n()
const router = useRouter()
const communities = ref([])
const selectedCommunityId = ref(null)
const statistics = ref({})
const lockersStats = ref({})
const loading = ref(true)
const community = ref(null)
const usersCount = ref(0)
const chartLoading = ref(false)
const selectedDays = ref(30)
const occupancyData = ref([])
const chartReady = ref(false)

const dayOptions = [
  { title: 'dashboard.periodSelector.last30Days', value: 30 },
  { title: 'dashboard.periodSelector.last60Days', value: 60 },
  { title: 'dashboard.periodSelector.last90Days', value: 90 }
]

const chartData = ref({
  labels: [],
  datasets: [{
    label: 'Hours of occupancy',
    data: [],
    borderColor: 'rgba(33, 150, 243, 0.6)',
    tension: 0.4,
    fill: true,
    backgroundColor: (context) => {
      const chart = context.chart
      const { ctx, chartArea } = chart
      if (!chartArea) return null
      
      const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top)
      gradient.addColorStop(0, 'rgba(33, 150, 243, 0)')
      gradient.addColorStop(1, 'rgba(33, 150, 243, 0.1)')
      return gradient
    },
    pointRadius: 4,
    pointHoverRadius: 6,
    borderWidth: 2
  }]
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: (context) => `${context.parsed.y} hours of occupancy`
      }
    }
  },
  elements: {
    line: {
      backgroundColor: 'rgba(var(--v-theme-surface), 0.8)'
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: 'Hours of occupancy'
      },
      ticks: {
        stepSize: 10
      },
      grid: {
        display: false
      }
    },
    x: {
      grid: {
        display: false
      },
      ticks: {
        maxRotation: 45,
        minRotation: 45,
        callback: function(value, index) {
          return index % 3 === 0 ? this.getLabelForValue(value) : ''
        }
      }
    }
  },
  interaction: {
    intersect: false,
    mode: 'index'
  }
}

const updateChartData = (data) => {
  if (!data || !data.length) return
  console.log('Updating chart with data:', data)
  
  const dates = Array.from({ length: selectedDays.value }).map((_, index) => {
    const date = new Date()
    date.setDate(date.getDate() - (selectedDays.value - 1 - index))
    return date.toLocaleDateString()
  })
  
  chartData.value = {
    labels: dates,
    datasets: [{
      label: 'Hours of occupancy',
      data: data,
      borderColor: 'rgba(33, 150, 243, 0.6)',
      tension: 0.4,
      fill: true,
      backgroundColor: (context) => {
        const chart = context.chart
        const { ctx, chartArea } = chart
        if (!chartArea) return null
        
        const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top)
        gradient.addColorStop(0, 'rgba(33, 150, 243, 0)')
        gradient.addColorStop(1, 'rgba(33, 150, 243, 0.1)')
        return gradient
      },
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2
    }]
  }
  console.log('Chart data updated:', chartData.value)
  chartReady.value = true
}

const fetchCommunity = async () => {
  try {
    const response = await api.get('/api/v1/community/')
    const communities = response.data.results
    // Show information only if the user has one community
    if (communities.length === 1) {
      community.value = communities[0]
      // Fetch users count after we know the community
      await fetchUsers()
    }
  } catch (error) {
    console.error('Failed to fetch community:', error)
  }
}

const fetchUsers = async () => {
  try {
    const response = await api.get('/api/v1/user/')
    usersCount.value = response.data.count
  } catch (error) {
    console.error('Failed to fetch users:', error)
  }
}

const fetchCommunities = async () => {
  try {
    const response = await api.get('/api/v1/community/')
    communities.value = response.data.results.map(community => ({
      title: community.name || community.title,
      value: community.id
    }))
    
    if (communities.value.length > 0) {
      selectedCommunityId.value = communities.value[0].value
    }
  } catch (error) {
    console.error('Failed to fetch communities:', error)
  }
}

const handleCommunityChange = async (communityId) => {
  await Promise.all([
    fetchStatistics(),
    fetchOccupancyData()
  ])
}

const fetchStatistics = async () => {
  loading.value = true
  try {
    const params = userStore.isAdmin ? { community_id: selectedCommunityId.value } : {}
    const response = await api.get('/api/v1/statistic/parcel/', { params })
    statistics.value = response.data
  } catch (error) {
    console.error('Failed to fetch statistics:', error)
  } finally {
    loading.value = false
  }
}

const fetchLockersStats = async () => {
  try {
    const response = await api.get('/api/v1/statistic/lockers/')
    lockersStats.value = response.data
  } catch (error) {
    console.error('Failed to fetch lockers statistics:', error)
  }
}

const fetchOccupancyData = async () => {
  chartLoading.value = true
  try {
    const params = userStore.isAdmin && selectedCommunityId.value ? 
      { community: selectedCommunityId.value } : {}
    const response = await api.get(`/api/v1/statistic/occupancy/${selectedDays.value}/`, { params })
    if (response.data && response.data.result) {
      updateChartData(response.data.result)
    } else {
      updateChartData([])
    }
  } catch (error) {
    console.error('Failed to fetch occupancy data:', error)
    updateChartData([])
  } finally {
    chartLoading.value = false
  }
}

const handlePeriodChange = async (days) => {
  selectedDays.value = days
  await fetchOccupancyData()
}

// Function to navigate to the packages page with a filter set
const navigateToPackages = (status) => {
  // Simply navigate to the packages page without filter parameters
  router.push('/packages')
}

onMounted(async () => {
  if (userStore.isAdmin) {
    await fetchCommunities()
  }
  await fetchStatistics()
  await fetchLockersStats()
  await fetchOccupancyData()
  // Load user data if it hasn't been loaded yet
  if (!userStore.profile) {
    userStore.fetchProfile()
  }
})
</script>

<style scoped>
.text-medium-emphasis {
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.stat-card {
  position: relative;
  overflow: visible;
  padding-top: 20px;
}

.stat-icon-wrapper {
  position: absolute;
  top: -20px;
  left: 20px;
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14);
}

.blue-gradient {
  background: linear-gradient(45deg, #2196F3, #1976D2);
}

.orange-gradient {
  background: linear-gradient(45deg, #FF9800, #F57C00);
}

.green-gradient {
  background: linear-gradient(45deg, #4CAF50, #388E3C);
}

.stat-content {
  padding-left: 100px !important;
  min-height: 100px;
}

.stat-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 2rem;
  font-weight: 600;
  margin: 8px 0;
  min-height: 48px;
  display: flex;
  align-items: center;
}

.stat-link-btn {
  transition: transform 0.2s ease;
  margin-left: 4px;
}

.stat-link-btn:hover {
  transform: scale(1.2);
}

.stat-link-btn:active {
  transform: scale(0.95);
}

.stat-description {
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.status-item {
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: default;
}

.status-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.status-icon {
  position: absolute;
  top: 8px;
  left: 8px;
  opacity: 0.8;
}

.status-value {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.2;
}

.status-label {
  font-size: 0.75rem;
  opacity: 0.8;
  margin-top: 4px;
  text-align: center;
}

.status-item.total {
  background: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
}

.status-item.success {
  background: rgba(var(--v-theme-success), 0.1);
  color: rgb(var(--v-theme-success));
}

.status-item.warning {
  background: rgba(var(--v-theme-warning), 0.1);
  color: rgb(var(--v-theme-warning));
}

.status-item.error {
  background: rgba(var(--v-theme-error), 0.1);
  color: rgb(var(--v-theme-error));
}

.status-item.info {
  background: rgba(var(--v-theme-info), 0.1);
  color: rgb(var(--v-theme-info));
}

.chart-container {
  height: 400px;
  position: relative;
}

.period-btn {
  min-width: 70px;
  font-size: 0.8125rem;
  letter-spacing: 0;
}

.period-toggle {
  border: thin solid rgba(var(--v-theme-primary), 0.12) !important;
}

.period-btn.v-btn--active {
  font-weight: 500;
}

.community-select {
  max-width: 300px;
}

.stats-card {
  border-radius: 8px;
}
</style> 